import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { flatSrc } from "@/lib/catalog";
import { artOf, zoneGeometry } from "@/lib/nameZones";
import { chromaOf } from "@/lib/chroma";
import { MOLDS } from "@/lib/molds";
import { ChromaPhoto } from "./ChromaPhoto";

// Desenho de cada peça (a partir das fotografias reais), preenchido com o tecido escolhido e com o nome personalizado.
// Atualiza em tempo real: padrão, nome e opções (fita, prendedor, nome na orelha...).

// Lado (em unidades do desenho) de cada repetição do tecido: os tecidos repetem-se sem espelho, para o desenho não virar um caleidoscópio.
const TILE = 150;
const UPLOAD_TILE = 80;
const INK = "#2B2420";
const CREAM = "#EFE6D6";
const WHITE = "#FBF8F3";
const BAND = "#D9A9A8";
const polar = (cx, cy, fn, n = 220) =>
  "M" + Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2;
    const r = fn(a);
    return `${(cx + r * Math.cos(a)).toFixed(1)} ${(cy + r * Math.sin(a)).toFixed(1)}`;
  }).join(" L") + " Z";


function Stitch({ d, color = "rgba(255,255,255,0.8)", w = 1.4, dash = "5 4" }) {
  return <path d={d} fill="none" stroke={color} strokeWidth={w} strokeDasharray={dash} strokeLinecap="round" />;
}

// Nome personalizado: a letra ajusta-se ao espaço. Sem nome, mostra uma sugestão discreta.
function Embroidery({ text, x, y, width, size = 40, rotate = 0, hint, show = true, color = INK }) {
  if (!show) return null;
  const t = (text ?? "").trim();
  const value = t || hint;
  if (!value) return null;
  // A letra manuscrita ocupa cerca de 0,42 x o tamanho por letra: o nome encolhe para caber sempre na largura da zona.
  const fs = Math.max(10, Math.min(size, width / (Math.max(value.length, 3) * 0.42)));
  return (
    <text x={x} y={y} textAnchor="middle" transform={rotate ? `rotate(${rotate} ${x} ${y})` : undefined} fontFamily="'Sacramento', cursive" fontSize={fs} fill={color} stroke={color} strokeWidth={fs * 0.012} dominantBaseline="central" opacity={t ? 0.95 : 0.28} data-testid={t ? "designer-name" : "designer-name-hint"}>
      {value}
    </text>
  );
}

// Pesponto: linha tracejada a distância constante (inset) de toda a borda do molde, também à volta do decote. Unidades = as do caminho (mm).
const Stitched = ({ d, inset, id, color = "rgba(255,255,255,0.88)" }) => {
  const sw = inset * 2;
  return (
    <g>
      <defs>
        <clipPath id={`sc${id}`}><path d={d} /></clipPath>
        <mask id={`sm${id}`} maskUnits="userSpaceOnUse" x="-20" y="-20" width="400" height="400">
          <rect x="-20" y="-20" width="400" height="400" fill="black" />
          <path d={d} fill="none" stroke="white" strokeWidth={sw + 0.6} strokeLinejoin="round" />
          <path d={d} fill="none" stroke="black" strokeWidth={sw - 0.6} strokeLinejoin="round" />
        </mask>
      </defs>
      <g clipPath={`url(#sc${id})`}>
        <path d={d} fill="none" stroke={color} strokeWidth={sw + 0.6} strokeDasharray="3.4 2.6" strokeLinejoin="round" mask={`url(#sm${id})`} />
      </g>
    </g>
  );
};

// Etiqueta de cetim das peças Babybib (branca, com "Babybib" a preto): len = lado maior, thick = lado menor, no mesmo sistema de unidades do desenho.
const SatinLabel = ({ x, y, len = 26, thick = 10, rotate = 0 }) => {
  const fs = Math.min(thick * 0.66, (len * 0.82) / 3.7);
  return (
    <g transform={`rotate(${rotate} ${x} ${y})`} data-testid="satin-label">
      <rect x={x - len / 2} y={y - thick / 2} width={len} height={thick} fill="#FCFAFF" stroke="rgba(64,55,49,0.22)" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
      <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontFamily="'Pacifico', 'Sacramento', cursive" fontSize={fs} fill="#1E1B1B">Babybib</text>
    </g>
  );
};

const Snap = ({ cx, cy, r = 7 }) => (
  <g>
    <circle cx={cx} cy={cy} r={r} fill="#F4F1EC" stroke="rgba(64,55,49,0.3)" strokeWidth="1" />
    <circle cx={cx} cy={cy} r={r * 0.45} fill="rgba(64,55,49,0.12)" />
  </g>
);

const Label = ({ x, y, rotate = 0 }) => (
  <g transform={`rotate(${rotate} ${x} ${y})`}>
    <rect x={x - 26} y={y - 11} width="52" height="22" rx="2" fill="#F3EBDD" stroke="rgba(64,55,49,0.18)" />
    <text x={x} y={y + 5} textAnchor="middle" fontFamily="'Sacramento', cursive" fontSize="15" fill={INK}>Babybib</text>
  </g>
);

// Faixa de elástico franzido (scrunchie) dos porta-fraldas.
const Scrunchie = ({ x, y, w, h = 36, vertical = false }) => {
  const ruffle = Array.from({ length: Math.floor((vertical ? h : w) / 9) }, (_, i) => i * 9);
  return (
    <g transform={vertical ? `translate(${x} ${y})` : `translate(${x} ${y})`}>
      <rect width={w} height={h} fill={BAND} />
      {ruffle.map((p) => (vertical ? <path key={p} d={`M0 ${p} Q${w / 2} ${p + 5} ${w} ${p}`} stroke="rgba(255,255,255,0.4)" fill="none" /> : <path key={p} d={`M${p} 0 Q${p + 5} ${h / 2} ${p} ${h}`} stroke="rgba(255,255,255,0.4)" fill="none" />))}
      <rect width={w} height={h} fill="none" stroke="rgba(64,55,49,0.18)" />
    </g>
  );
};

/* ---------- peças ---------- */

// O ursinho tem 85 mm de largura no molde: 200 unidades do desenho.
const BEAR_K = 200 / 85;
const BEAR_Y = 80;
function Bear({ F, v, part = "conjunto" }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const long = v.fita === "comprida";
  const end = 70 + (long ? 360 : 290);
  const wood = v.prendedor !== "plastico";
  const withBelt = v.adaptacao === "fio";
  const showBear = part !== "fita", showRibbon = part !== "urso";
  // Só o ursinho: sem fita nem prendedor. Só a fita: sem ursinho, o nome ocupa a fita toda.
  const ribbonTop = 70;
  const rw = showBear ? 28 : 46; // sozinha, a fita aparece mais larga para se ver bem
  const ribbonStart = showBear ? BEAR_Y + MOLDS.ursinho.h * BEAR_K : 90;
  return (
    <g>
      {/* fita */}
      {showRibbon && <rect x={200 - rw / 2} y={ribbonTop} width={rw} height={end - ribbonTop} fill={F} stroke="rgba(64,55,49,0.18)" />}
      {showRibbon && <Stitch d={`M${200 - rw / 2 + 4} ${ribbonTop + 2} V${end - 2} M${200 + rw / 2 - 4} ${ribbonTop + 2} V${end - 2}`} w="1" dash="4 4" />}
      {/* prendedor */}
      {!showRibbon ? null : wood ? (
        <g>
          <rect x="190" y="52" width="20" height="26" rx="3" fill="#B9B3AA" stroke="rgba(64,55,49,0.3)" />
          <circle cx="200" cy="34" r="22" fill="#D9B98C" stroke="rgba(64,55,49,0.3)" />
          <ellipse cx="193" cy="27" rx="7" ry="5" fill="rgba(255,255,255,0.35)" />
        </g>
      ) : (
        <g>
          <rect x="188" y="14" width="24" height="52" rx="8" fill={WHITE} stroke="rgba(64,55,49,0.3)" />
          <rect x="193" y="52" width="14" height="22" rx="2" fill="#B9B3AA" stroke="rgba(64,55,49,0.3)" />
        </g>
      )}
      {/* cabeça e orelhas: contorno do molde real do ursinho (85 mm de largura) */}
      {showBear && <g style={{ filter: "drop-shadow(0 6px 8px rgba(64,55,49,0.18))" }}>
        <g transform={`translate(${200 - (MOLDS.ursinho.w * BEAR_K) / 2} ${BEAR_Y}) scale(${BEAR_K})`}>
          <path d={MOLDS.ursinho.d} fill={F} stroke="rgba(64,55,49,0.2)" strokeWidth="1.4" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
          <Stitched d={MOLDS.ursinho.d} inset={3.4} id={uid} />
          <SatinLabel x={MOLDS.ursinho.w * 0.075} y={MOLDS.ursinho.h * 0.62} len={20} thick={7.5} rotate={90} />
        </g>
      </g>}
      {showBear && <Snap cx="200" cy={BEAR_Y + MOLDS.ursinho.h * BEAR_K * 0.2} />}
      <Embroidery text={v.nomeUrsinho} hint="Nome" show={showBear && v.nomeUrsinhoOn} x="200" y={BEAR_Y + MOLDS.ursinho.h * BEAR_K * 0.6} width={MOLDS.ursinho.w * BEAR_K * 0.62} size={34} />
      {/* fita: nome e remate */}
      <Embroidery text={v.nomeFita} hint="Nome" show={showRibbon && v.nomeFitaOn} x="200" y={(ribbonStart + (end - 31)) / 2 + 3} width={end - 31 - ribbonStart - 10} size={26} rotate={90} />
      {!showRibbon ? null : withBelt ? (
        <path d={`M200 ${end} v10 q-26 36 -26 56 q0 18 26 18 q26 0 26 -18 q0 -20 -26 -56`} fill="none" stroke={WHITE} strokeWidth="3" strokeLinecap="round" />
      ) : (
        <Snap cx="200" cy={end - 18} r={9} />
      )}
    </g>
  );
}

// Babetes: contornos dos moldes reais (formato flor e formato lenço), à mesma escala (o decote tem 88 mm nos dois).
const BIB_K = 1.6; // unidades do desenho por mm
const BIB_Y = { flor: 78, lenco: 50 };
function Bib({ F, shape, scale = 1, tx = 0, ty = 0 }) {
  const m = shape === "lenco" ? MOLDS.lenco : MOLDS.flor;
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const x0 = 200 - (m.w * BIB_K) / 2;
  const y0 = BIB_Y[shape === "lenco" ? "lenco" : "flor"];
  return (
    <g transform={`translate(${tx} ${ty}) scale(${scale})`}>
      <g transform={`translate(${x0} ${y0}) scale(${BIB_K})`}>
        <path d={m.d} fill={F} stroke="rgba(64,55,49,0.18)" strokeWidth="1.2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        <Stitched d={m.d} inset={4.5} id={uid} />
        {/* aba de fecho sobre as duas pontas, com mola */}
        <rect x={m.w * 0.455} y={m.h * 0.012} width={m.w * 0.2} height={m.h * 0.062} rx={m.h * 0.03} fill={F} stroke="rgba(64,55,49,0.2)" vectorEffect="non-scaling-stroke" />
        <circle cx={m.w * 0.6} cy={m.h * 0.043} r={m.w * 0.026} fill="#F4F1EC" stroke="rgba(64,55,49,0.3)" vectorEffect="non-scaling-stroke" />
        {/* etiqueta na lateral */}
        <SatinLabel x={m.w * 0.968} y={m.h * 0.4} len={26} thick={10} rotate={90} />
      </g>
    </g>
  );
}

// Babete com frente e verso: sem verso é só o babete; com verso, vira (escala em X) e troca o tecido a meio da volta.
function FlipBib({ shape, front, back, side, flip }) {
  if (!back) return <Bib F={front} shape={shape} />;
  return (
    <g>
      {/* invisível: mantém o enquadramento estável durante a volta */}
      <g opacity="0"><Bib F="none" shape={shape} /></g>
      <g key={flip} className={flip ? "bb-flip" : undefined}><Bib F={side === "verso" ? back : front} shape={shape} /></g>
    </g>
  );
}

function BibPack({ Fs, shapes }) {
  return (
    <g>
      {[[0, 0], [190, 0], [0, 235], [190, 235]].map(([x, y], i) => (
        <Bib key={i} F={Fs[i]} shape={shapes[i]} scale={0.5} tx={x + 5} ty={y - 5} />
      ))}
    </g>
  );
}

// Tapa-fraldas: cós franzido com elástico e pernas com elástico, volume nas coxas.
function Bloomers({ F }) {
  const body = "M72 168 C34 226 26 312 60 368 Q122 396 186 362 Q200 354 214 362 Q278 396 340 368 C374 312 366 226 328 168 Z";
  const gathers = Array.from({ length: 22 }, (_, i) => 78 + i * 11.6);
  return (
    <g style={{ filter: "drop-shadow(0 8px 10px rgba(64,55,49,0.16))" }}>
      <path d="M62 128 H338 V150 H62 Z" fill="#F4F0EA" stroke="rgba(64,55,49,0.14)" />
      <path d={body} fill={F} stroke="rgba(64,55,49,0.18)" strokeWidth="1.2" />
      <path d="M66 126 Q86 118 106 126 T146 126 T186 126 T226 126 T266 126 T306 126 T334 126 V176 H66 Z" fill={F} stroke="rgba(64,55,49,0.2)" />
      {gathers.map((x) => <path key={x} d={`M${x} 128 Q${x + 4} 152 ${x} 174`} fill="none" stroke="rgba(64,55,49,0.14)" strokeWidth="1.4" />)}
      <path d="M66 176 H334" stroke="rgba(255,255,255,0.8)" strokeDasharray="5 4" fill="none" />
      <path d="M62 366 Q122 394 186 360 M214 360 Q278 394 338 366" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.4" strokeDasharray="5 4" />
      <path d="M62 366 Q122 394 186 360 M214 360 Q278 394 338 366" fill="none" stroke="rgba(64,55,49,0.16)" strokeWidth="7" opacity=".45" />
    </g>
  );
}

// Fita franzida (em tecido liso rosa, como nas fotografias) à volta do muda-fraldas.
function Gather({ x, y, w, h, fill = BAND }) {
  const n = Math.floor(h / 9);
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="5" fill={fill} />
      {Array.from({ length: n }, (_, i) => <path key={i} d={`M${x} ${y + i * 9 + 4} Q${x + w / 2} ${y + i * 9 + 10} ${x + w} ${y + i * 9 + 4}`} fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" />)}
      <rect x={x} y={y} width={w} height={h} rx="5" fill="none" stroke="rgba(64,55,49,0.2)" />
    </g>
  );
}

const Tag = ({ x, y, w = 56, h = 26, fs = 17 }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} fill="#F6EFDF" stroke="rgba(64,55,49,0.2)" />
    <text x={x + w / 2} y={y + h * 0.68} textAnchor="middle" fontFamily="'Sacramento', cursive" fontSize={fs} fill={INK}>Babybib</text>
  </g>
);

// Muda-fraldas portátil: retângulo dobrado, com a fita franzida e a etiqueta.
// Muda-fraldas aberto: o tapete esticado (dobrado em três, com os vincos) e a etiqueta.
function MatOpen({ F, B = BAND }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const d = "M70 140 H330 Q346 140 346 156 V324 Q346 340 330 340 H70 Q54 340 54 324 V156 Q54 140 70 140 Z";
  return (
    <g>
      <path d={d} fill={F} stroke="rgba(64,55,49,0.18)" strokeWidth="1.2" />
      {[148, 252].map((x) => (
        <g key={x}>
          <path d={`M${x} 142 V338`} stroke="rgba(64,55,49,0.16)" strokeWidth="2.2" />
          <path d={`M${x + 2.2} 142 V338`} stroke="rgba(255,255,255,0.45)" strokeWidth="1.4" />
        </g>
      ))}
      <Stitched d={d} inset={8} id={uid} />
      <Tag x={268} y={298} w={58} h={24} />
    </g>
  );
}

function ChangingMat({ F, B = BAND, tx = 0, ty = 0, s = 1 }) {
  return (
    <g transform={`translate(${tx} ${ty}) scale(${s})`}>
      <rect x="92" y="70" width="212" height="356" rx="12" fill={F} stroke="rgba(64,55,49,0.18)" />
      <rect x="99" y="77" width="198" height="342" rx="5" fill="none" stroke="rgba(255,255,255,0.85)" strokeDasharray="5 4" />
      <Gather x={112} y={64} w={34} h={368} fill={B} />
      <Tag x={160} y={380} />
    </g>
  );
}

// Porta-fraldas: carteira aberta, com a janela oval para os toalhetes e o bolso para a fralda.
function DiaperCase({ F, tx = 0, ty = 0, s = 1 }) {
  return (
    <g transform={`translate(${tx} ${ty}) scale(${s})`}>
      <rect x="36" y="150" width="328" height="244" rx="16" fill={F} stroke="rgba(64,55,49,0.18)" />
      <path d="M222 232 L364 206 V378 Q364 394 348 394 H238 Q222 394 222 378 Z" fill={F} stroke="rgba(64,55,49,0.2)" />
      <rect x="43" y="157" width="314" height="230" rx="9" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.3" strokeDasharray="5 4" />
      <ellipse cx="120" cy="268" rx="54" ry="36" transform="rotate(-12 120 268)" fill="#E9E4DB" stroke="rgba(64,55,49,0.22)" strokeWidth="2" />
      <ellipse cx="120" cy="268" rx="62" ry="43" transform="rotate(-12 120 268)" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.3" strokeDasharray="4 3.5" />
      <path d="M222 232 L364 206" stroke="rgba(64,55,49,0.18)" strokeWidth="1.2" />
    </g>
  );
}

function DiaperSet({ F, B }) {
  return (
    <g>
      <ChangingMat F={F} B={B} tx={14} ty={22} s={0.84} />
      <DiaperCase F={F} tx={168} ty={238} s={0.6} />
    </g>
  );
}

// Porta-documentos aberto, como na fotografia: duas folhas em tecido com o vinco ao meio, bolso em bombazina com mola (em baixo, à direita)
// e a fita com mola que o fecha. O nome vai por dentro, na folha da esquerda, acima da etiqueta; mostra-se também a capa.
const ViewLabel = ({ x = 200, y, children }) => <text x={x} y={y} textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="600" letterSpacing="2.5" fill="rgba(64,55,49,0.7)">{children}</text>;

function DocHolder({ F, E, name }) {
  const CORD = "#CDB9A3";
  const C = E ?? F;
  const cover = (
      <g transform="translate(0 -250)">
        <g>
          <rect x="334" y="236" width="62" height="30" rx="6" fill={C} stroke="rgba(64,55,49,0.18)" />
          <rect x="337" y="239" width="56" height="24" rx="4" fill="none" stroke="rgba(255,255,255,0.85)" strokeDasharray="4 3.5" />
          <Snap cx="384" cy="251" r={6} />
        </g>
        <rect x="40" y="150" width="320" height="200" rx="10" fill={C} stroke="rgba(64,55,49,0.18)" />
        <rect x="47" y="157" width="306" height="186" rx="3" fill="none" stroke="rgba(255,255,255,0.85)" strokeDasharray="5 4" />
      </g>
  );
  return (
    <g>
      <ViewLabel y="-120">CAPA</ViewLabel>
      {cover}
      <ViewLabel y="126">INTERIOR</ViewLabel>
      {E && (
        <g>
          <rect x="30" y="140" width="340" height="220" rx="14" fill={E} stroke="rgba(64,55,49,0.2)" />
          <rect x="38" y="148" width="324" height="204" rx="6" fill="none" stroke="rgba(255,255,255,0.7)" strokeDasharray="5 4" />
        </g>
      )}
      <g>
        <rect x="334" y="212" width="62" height="32" rx="6" fill={F} stroke="rgba(64,55,49,0.18)" />
        <rect x="338" y="216" width="54" height="24" rx="3" fill="none" stroke="rgba(255,255,255,0.85)" strokeDasharray="4 3.5" />
        <Snap cx="384" cy="228" r={6} />
      </g>
      <rect x="40" y="150" width="320" height="200" rx="10" fill={F} stroke="rgba(64,55,49,0.18)" />
      <rect x="47" y="157" width="306" height="186" rx="3" fill="none" stroke="rgba(255,255,255,0.85)" strokeDasharray="5 4" />
      {/* folha da esquerda: bolso cosido, com o nome */}
      <rect x="52" y="178" width="132" height="156" rx="3" fill={F} stroke="rgba(64,55,49,0.2)" />
      <rect x="56" y="182" width="124" height="148" rx="2" fill="none" stroke="rgba(255,255,255,0.9)" strokeDasharray="4 3.5" />
      <path d="M200 152 V348" stroke="rgba(64,55,49,0.16)" strokeWidth="2.2" />
      <path d="M202.2 152 V348" stroke="rgba(255,255,255,0.45)" strokeWidth="1.4" />
      {/* bolso em bombazina com mola, até à margem direita */}
      <path d="M236 224 H358 V340 Q358 344 354 344 H240 Q236 344 236 340 Z" fill={CORD} stroke="rgba(64,55,49,0.22)" />
      {Array.from({ length: 14 }, (_, i) => <path key={i} d={`M238 ${230 + i * 8} H356`} stroke="rgba(64,55,49,0.1)" strokeWidth="1.6" />)}
      <rect x="240" y="228" width="114" height="112" rx="2" fill="none" stroke="rgba(255,255,255,0.8)" strokeDasharray="4 3.5" />
      <Snap cx="300" cy="240" r={7} />
      <Tag x={58} y={312} w={46} h={18} fs={13} />
      <Embroidery text={name} hint="Nome" x="118" y="214" width="112" size={40} color="#4A2020" />
    </g>
  );
}

// Saquinho de roupa: base em algodão cru, forro em tecido virado para fora, como na fotografia.
const POUCH_Y = { centro: 346, cima: 292, baixo: 402 };
function Pouch({ F, E, name, scale = 1, tx = 0, ty = 0, showName = true, place = "centro" }) {
  return (
    <g transform={`translate(${tx} ${ty}) scale(${scale})`} style={{ filter: "drop-shadow(0 10px 12px rgba(64,55,49,0.18))" }}>
      <path d="M104 236 H296 L304 420 Q304 440 284 440 H116 Q96 440 96 420 Z" fill={E ?? CREAM} stroke="rgba(64,55,49,0.2)" />
      <path d="M104 236 H296 L304 420 Q304 440 284 440 H116 Q96 440 96 420 Z" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" strokeDasharray="4 3.5" transform="translate(200 338) scale(0.94) translate(-200 -338)" />
      <rect x="100" y="146" width="200" height="96" rx="8" fill={F} stroke="rgba(64,55,49,0.2)" />
      <ellipse cx="200" cy="148" rx="100" ry="9" fill={F} stroke="rgba(64,55,49,0.2)" />
      <ellipse cx="200" cy="148" rx="100" ry="9" fill="rgba(64,55,49,0.16)" />
      <path d="M102 236 H298" stroke="rgba(64,55,49,0.18)" strokeWidth="3" />
      <SatinLabel x={302} y={310} len={34} thick={13} rotate={90} />
      <Embroidery text={name} hint="Nome" show={showName} x="200" y={POUCH_Y[place] ?? 346} width="120" size={42} color="#3A2418" />
    </g>
  );
}

function PouchPack({ Fs }) {
  return (
    <g>
      <Pouch F={Fs[0]} scale={0.62} tx={-12} ty={92} showName={false} />
      <Pouch F={Fs[1]} scale={0.62} tx={150} ty={92} showName={false} />
      <Pouch F={Fs[2]} scale={0.62} tx={70} ty={185} showName={false} />
    </g>
  );
}

// Coelhinho (proporções da fotografia): felpa branca, orelhas compridas forradas a tecido, olhos fechados, nariz rosa,
// gola em rolo franzido e contorno fofo.
function Bunny({ F, v }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const plush = `url(#pl${uid})`;
  const EDGE = "rgba(64,55,49,0.12)";
  // Rolo franzido: ondas largas e suaves, como um scrunchie visto de frente.
  const roll = (rx, ry, k, n) => "M" + Array.from({ length: 200 }, (_, i) => {
    const t = (i / 200) * Math.PI * 2;
    const r = 1 + k * Math.sin(t * n);
    return `${(200 + rx * r * Math.cos(t)).toFixed(1)} ${(324 + ry * r * Math.sin(t)).toFixed(1)}`;
  }).join(" L") + " Z";
  return (
    <g style={{ filter: "drop-shadow(0 10px 12px rgba(64,55,49,0.18))" }}>
      <defs>
        <radialGradient id={`pl${uid}`} cx="42%" cy="32%" r="80%"><stop offset="0" stopColor="#FFFFFF" /><stop offset="0.7" stopColor="#F6F3EE" /><stop offset="1" stopColor="#E9E4DB" /></radialGradient>
        <filter id={`fz${uid}`} x="-5%" y="-5%" width="110%" height="110%"><feTurbulence type="fractalNoise" baseFrequency="0.09" numOctaves="2" seed="3" result="n" /><feDisplacementMap in="SourceGraphic" in2="n" scale="3.2" /></filter>
        <clipPath id={`cc${uid}`}><rect x="0" y="302" width="400" height="120" /></clipPath>
      </defs>
      {/* orelhas: costas em felpa, frente em tecido */}
      <g filter={`url(#fz${uid})`}>
        <g transform="rotate(-8 166 214)">
          <ellipse cx="166" cy="116" rx="31" ry="104" fill={plush} stroke={EDGE} />
        </g>
        <g transform="rotate(8 234 214)">
          <ellipse cx="234" cy="116" rx="31" ry="104" fill={plush} stroke={EDGE} />
        </g>
      </g>
      <g transform="rotate(-8 166 214)">
        <ellipse cx="164" cy="120" rx="23" ry="92" fill={F} stroke="rgba(64,55,49,0.16)" />
        <ellipse cx="164" cy="120" rx="18" ry="86" fill="none" stroke="rgba(255,255,255,0.8)" strokeDasharray="4 3" />
      </g>
      <g transform="rotate(8 234 214)">
        <ellipse cx="236" cy="120" rx="23" ry="92" fill={F} stroke="rgba(64,55,49,0.16)" />
        <ellipse cx="236" cy="120" rx="18" ry="86" fill="none" stroke="rgba(255,255,255,0.8)" strokeDasharray="4 3" />
      </g>
      <Embroidery text={v.nomeOrelha} hint="Nome" show={v.nomeOrelhaOn} x={v.orelha === "direita" ? 237 : 163} y="118" width="150" size={26} rotate={v.orelha === "direita" ? -82 : -98} />
      {/* corpo (peras), braços e pés */}
      <g filter={`url(#fz${uid})`}>
        <path d="M200 312 C268 312 282 372 280 424 C279 462 256 486 200 486 C144 486 121 462 120 424 C118 372 132 312 200 312 Z" fill={plush} stroke={EDGE} />
        <ellipse cx="128" cy="376" rx="17" ry="35" fill={plush} stroke={EDGE} transform="rotate(14 128 376)" />
        <ellipse cx="272" cy="376" rx="17" ry="35" fill={plush} stroke={EDGE} transform="rotate(-14 272 376)" />
        <ellipse cx="200" cy="248" rx="94" ry="76" fill={plush} stroke={EDGE} />
      </g>
      <path d="M200 446 V484" stroke="rgba(64,55,49,0.1)" strokeWidth="1.4" fill="none" />
      {/* cara */}
      <path d="M163 254 q13 10 26 0 M211 254 q13 10 26 0" stroke="#2B2420" strokeWidth="1.9" fill="none" strokeLinecap="round" />
      <path d="M166 257 l-4 4 M174 261 l-2 5 M234 257 l4 4 M226 261 l2 5" stroke="#2B2420" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M194 270 h12 l-6 8 z" fill="#E8A5A5" stroke="#E8A5A5" strokeLinejoin="round" />
      {/* gola: rolo de tecido franzido por cima do queixo */}
      <g clipPath={`url(#cc${uid})`}>
        <path d={roll(102, 34, 0.05, 9)} fill={F} stroke="rgba(64,55,49,0.2)" />
        <path d={roll(102, 34, 0.05, 9)} fill="rgba(64,55,49,0.07)" />
        {[-78, -54, -28, 0, 28, 54, 78].map((x) => <path key={x} d={`M${200 + x} 312 Q${200 + x * 1.08} 340 ${200 + x * 0.96} 357`} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.6" />)}
        <path d={roll(86, 22, 0.03, 9)} fill="none" stroke="rgba(255,255,255,0.65)" strokeDasharray="4 3" />
        <path d="M100 328 Q200 372 300 328" fill="none" stroke="rgba(64,55,49,0.18)" strokeWidth="2" />
      </g>
    </g>
  );
}

const read = (o, id) => {
  const c = o?.[id];
  return c ? { choice: c.choiceId, text: c.text ?? "" } : { choice: null, text: "" };
};

export function ProductDesigner({ product, texture, name = "", zone, options, patternName, slotTextures = {}, slotModels = {}, view = null }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  // Enquadramento automático: o desenho ocupa o quadro (mede a peça e ajusta o viewBox, com uma margem de 9 %).
  const artRef = useRef(null);
  const [vb, setVb] = useState("0 0 400 500");
  // Corre a cada desenho (só atualiza se a medida mudar), por isso não leva lista de dependências.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const g = artRef.current;
    if (!g) return;
    try {
      const b = g.getBBox();
      if (!b.width || !b.height) return;
      const pad = Math.max(b.width, b.height) * 0.09;
      const next = `${(b.x - pad).toFixed(1)} ${(b.y - pad).toFixed(1)} ${(b.width + pad * 2).toFixed(1)} ${(b.height + pad * 2).toFixed(1)}`;
      setVb((cur) => (cur === next ? cur : next));
    } catch {
      // sem medida: mantém o enquadramento
    }
  });
  // Babete reversível: tem sempre Frente e Verso para alternar (o babete vira); ao escolher o verso, ou ao mudar o padrão de um lado, vira para esse lado.
  const isBibPack = product.slug === "pack-4-babetes";
  const reversible = product.category === "babetes" && !isBibPack && !!product.slots?.some((s) => s.onlyWith);
  // Pack: alguns babetes podem ter verso; o desenho mostra a frente de todos ou o verso (os que n�o t�m verso mant�m a frente).
  const packVersos = isBibPack ? [1, 2, 3, 4].map((n) => slotTextures[`babete-${n}-verso`]) : [];
  const hasPackVerso = packVersos.some(Boolean);
  const versoTex = isBibPack ? (hasPackVerso ? packVersos.join("|") : undefined) : slotTextures.verso;
  const [side, setSide] = useState("frente");
  const [flip, setFlip] = useState(0);
  const timer = useRef(null);
  const goSide = (want) => {
    if (want === side) return;
    setFlip((f) => f + 1);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setSide(want), 280);
  };
  const prevSides = useRef({ versoTex, texture });
  useEffect(() => {
    const prev = prevSides.current;
    prevSides.current = { versoTex, texture };
    const want = !versoTex ? "frente" : versoTex !== prev.versoTex ? "verso" : texture !== prev.texture ? "frente" : null;
    if (want) goSide(want);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versoTex, texture]);
  useEffect(() => () => clearTimeout(timer.current), []);
  // Cada parte da peça (elástico, babetes de um pack...) pode ter o seu tecido: um padrão SVG por tecido diferente.
  const texFor = (slotId) => slotTextures[slotId] ?? texture;
  const texList = [...new Set([texture, ...Object.values(slotTextures)].filter(Boolean))];
  const fabFor = (t) => `url(#f${uid}-${Math.max(0, texList.indexOf(t))})`;
  const fab = fabFor(texture);
  const nu = read(options, "nome-ursinho"), nf = read(options, "nome-fita"), no = read(options, "nome-orelha");
  const v = {
    fita: read(options, "fita").choice,
    prendedor: read(options, "prendedor").choice,
    adaptacao: read(options, "adaptacao").choice,
    formato: read(options, "formato").choice,
    view,
    orelha: read(options, "orelha-nome").choice,
    nomeUrsinhoOn: nu.choice === "com-nome", nomeUrsinho: nu.text,
    nomeFitaOn: nf.choice === "sim", nomeFita: nf.text,
    nomeOrelhaOn: no.choice === "com-nome", nomeOrelha: no.text,
  };
  const slug = product.slug;
  const category = product.category;
  // Porta-documentos: o nome vai por dentro, acima da etiqueta (o campo do nome ou o texto da opção de nome).
  const nomeOpt = read(options, "nome");
  const docName = name || (nomeOpt.choice && nomeOpt.choice !== "sem-nome" ? nomeOpt.text : "");
  const kind = artOf(product, v.formato);
  // Muda-fraldas aberto: o nome vai no terço do meio (o fechado usa as zonas da fotografia).
  const OPEN_NAMES = { cima: { x: 200, y: 214, width: 88, size: 34 }, baixo: { x: 200, y: 272, width: 88, size: 34 } };
  const geo = product.personalization && !["urso", "coelho", "pack-babetes", "pack-saquinhos", "documentos", "muda-fraldas"].includes(kind) ? zoneGeometry(kind, zone) : null;
  const matClosed = product.personalization && kind === "muda-fraldas" ? zoneGeometry(kind, zone) : null;
  const matOpen = product.personalization && kind === "muda-fraldas" ? OPEN_NAMES[zone] ?? OPEN_NAMES.cima : null;
  let art;
  if (slug === "porta-chupetas" || category === "porta-chupetas") art = <Bear F={fab} v={v} part={slug === "ursinho" ? "urso" : slug === "fita-porta-chupetas" ? "fita" : "conjunto"} />;
  else if (slug === "babete-lenco" || (slug === "babete" && v.formato === "lenco")) art = <FlipBib shape="lenco" front={fab} back={reversible ? fabFor(versoTex ?? texture) : null} side={side} flip={flip} />;
  else if (slug === "pack-4-babetes") art = <BibPack Fs={[1, 2, 3, 4].map((n) => fabFor(side === "verso" && packVersos[n - 1] ? packVersos[n - 1] : texFor(`babete-${n}`)))} shapes={[1, 2, 3, 4].map((n, i) => slotModels[`babete-${n}`] ?? (i % 2 ? "lenco" : "flor"))} />;
  else if (category === "babetes") art = <FlipBib shape="flor" front={fab} back={reversible ? fabFor(versoTex ?? texture) : null} side={side} flip={flip} />;
  else if (category === "tapa-fraldas") art = <Bloomers F={fab} />;
  else if (slug === "muda-fraldas-portatil") art = (
    <g>
      <ViewLabel x={138} y={36}>FECHADO</ViewLabel>
      <g transform="translate(-60 0)">
        <ChangingMat F={fab} B={slotTextures.elastico ? fabFor(slotTextures.elastico) : undefined} />
        {matClosed && <Embroidery text={name} hint="Nome" x={matClosed.x} y={matClosed.y} width={matClosed.width} size={matClosed.size} rotate={matClosed.rotate} color={INK} />}
      </g>
      <ViewLabel x={430} y={36}>ABERTO</ViewLabel>
      <g transform="translate(230 0)">
        <MatOpen F={fab} B={slotTextures.elastico ? fabFor(slotTextures.elastico) : undefined} />
        {matOpen && <Embroidery text={name} hint="Nome" x={matOpen.x} y={matOpen.y} width={matOpen.width} size={matOpen.size} color={INK} />}
      </g>
    </g>
  );
  else if (slug === "conjunto-porta-fraldas-muda-fraldas") art = <DiaperSet F={fab} B={slotTextures.elastico ? fabFor(slotTextures.elastico) : undefined} />;
  else if (slug === "porta-documentos") art = <DocHolder name={docName} F={fabFor(texFor("interior"))} E={slotTextures.exterior ? fabFor(slotTextures.exterior) : undefined} />;
  else if (category === "porta-fraldas") art = (
    <g>
      <ViewLabel y={-206}>FORA</ViewLabel>
      <g transform="translate(-16 -352) scale(1.08)">
        <rect x="36" y="150" width="328" height="244" rx="16" fill={fab} stroke="rgba(64,55,49,0.18)" />
        <rect x="43" y="157" width="314" height="230" rx="9" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.3" strokeDasharray="5 4" />
        <Tag x={300} y={362} w={46} h={20} fs={13} />
      </g>
      <ViewLabel y={112}>DENTRO</ViewLabel>
      <g transform="translate(-16 -22) scale(1.08)"><DiaperCase F={fab} /></g>
    </g>
  );
  else if (slug === "pack-3-saquinhos") art = <PouchPack Fs={[1, 2, 3].map((n) => fabFor(texFor(`saquinho-${n}`)))} />;
  else if (category === "saquinhos") art = <Pouch F={fabFor(texFor("forro"))} E={slotTextures.exterior ? fabFor(slotTextures.exterior) : undefined} showName={false} />;
  else if (category === "coelhinhos") art = <Bunny F={fab} v={v} />;
  else if (slug === "lacinho" || category === "lacinhos") art = <g />; // s� a fotografia (ver CHROMA)
  else return null;
  const chroma = chromaOf(product);
  if (chroma && (!chroma.only || chroma.only(v))) {
    const ears = chroma.ears?.[v.orelha === "direita" ? "direita" : "esquerda"];
    const nz = product.personalization && chroma.names ? chroma.names[zone] ?? Object.values(chroma.names)[0] : null;
    return (
      <ChromaPhoto spec={chroma} textures={Object.fromEntries(chroma.zones.map((z) => [z.id, texFor(z.id)]))} label={`Fotografia de ${product.name}${patternName ? ` em ${patternName}` : ""}`}>
        {ears && <Embroidery text={v.nomeOrelha} hint="Nome" show={v.nomeOrelhaOn} x={ears.x} y={ears.y} width={190} size={44} rotate={ears.rotate} color={INK} />}
        {nz && <Embroidery text={name} hint="Nome" x={nz.x} y={nz.y} width={nz.width} size={nz.size} rotate={nz.rotate ?? 0} color={INK} />}
      </ChromaPhoto>
    );
  }
  return (
    <div className="absolute inset-0" data-testid="product-designer">
      {(reversible || hasPackVerso) && (
        <div className="bb-sidetag absolute left-3 top-3 z-10 flex gap-1 rounded-full bg-ivory/85 p-1 backdrop-blur-md" role="group" aria-label={isBibPack ? "Lado dos babetes" : "Lado do babete"} data-testid="designer-side">
          {[["frente", "Frente"], ["verso", "Verso"]].map(([id, label]) => (
            <button key={id} type="button" aria-pressed={side === id} data-testid={`designer-side-${id}`} onClick={() => goSide(id)} className={`min-h-[32px] rounded-full px-3 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${side === id ? "bg-bordeaux text-ivory" : "text-taupe-strong hover:text-bordeaux"}`}>{label}</button>
          ))}
        </div>
      )}
      <svg viewBox={vb} className="h-full w-full" role="img" aria-label={`Desenho de ${product.name}${patternName ? ` em ${patternName}` : ""}`}>
        <defs>
          <filter id={`w${uid}`} x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.011 0.017" numOctaves="3" seed="7" result="n" />
            <feDiffuseLighting in="n" lightingColor="#ffffff" surfaceScale="2.4" result="l"><feDistantLight azimuth="225" elevation="60" /></feDiffuseLighting>
            <feComponentTransfer in="l" result="lb"><feFuncR type="linear" slope="1.16" /><feFuncG type="linear" slope="1.16" /><feFuncB type="linear" slope="1.16" /></feComponentTransfer>
            <feComposite in="lb" in2="SourceAlpha" operator="in" result="lm" />
            <feBlend in="SourceGraphic" in2="lm" mode="multiply" />
          </filter>
          {texList.map((t, i) => {
            // Padrões carregados pela administradora: repetem-se mais pequenos (senão ampliam-se demais e perdem definição) e sem o corte das amostras.
            const up = t?.startsWith("img:");
            const T = up ? UPLOAD_TILE : TILE;
            const c = up ? 0 : 0.04;
            return (
              <pattern key={t} id={`f${uid}-${i}`} patternUnits="userSpaceOnUse" width={T} height={T}>
                {/* as amostras têm o rebordo mais escuro: corta-se 4% de cada lado para não aparecer a junta entre repetições */}
                <image href={flatSrc(t)} x={-T * c} y={-T * c} width={T * (1 + 2 * c)} height={T * (1 + 2 * c)} preserveAspectRatio="xMidYMid slice" />
              </pattern>
            );
          })}
        </defs>
        <g ref={artRef} filter={["babete-flor", "babete-lenco", "pack-babetes", "muda-fraldas", "conjunto", "porta-fraldas", "documentos"].includes(kind) ? undefined : `url(#w${uid})`}>
          {art}
          {geo && <Embroidery text={name} hint="Nome" x={geo.x} y={geo.y} width={geo.width} size={geo.size} rotate={geo.rotate} color={kind === "saquinho" ? "#3A2418" : INK} />}
        </g>
      </svg>
    </div>
  );
}
